import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Snackbar,
  Switch,
  Tooltip,
  Typography
} from '@mui/material';
import ViewCarouselOutlinedIcon from '@mui/icons-material/ViewCarouselOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import {
  deleteProductModule,
  getAboutTabs,
  getCustomPages,
  getProductModules,
  saveAboutTab,
  saveCustomPage,
  saveProductModule
} from '../../../services/ecommerceApi';
import type { CustomModule } from '../ECommercePage';
import ContentCardsDialog from './contentCards/ContentCardsDialog';
import ContentCardsStylePreview from './contentCards/ContentCardsStylePreview';
import { normalizeContentCardVariant } from './contentCards/contentCardStyleOptions';
import { getContentCardTemplate, getContentCardTemplateLabel } from './contentCards/contentCardTemplates';
import { getContentCardLayoutLabel, normalizeContentCardsLayout } from './contentCards/contentCardLayoutTemplates';
import {
  cleanContentCardsPayload,
  hasContentCardData,
  normalizeContentCardForEditor,
  parseContentCardsFromHtml,
  stripContentCardsFromHtml
} from './contentCards/contentCardsUtils';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';
import { useEditorAutosave } from '../utils/useEditorAutosave';
import {
  BANNER_PANEL_COLORS,
  DEFAULT_CONTENT_CARD,
  DEFAULT_CONTENT_CARDS_DIALOG,
  DEFAULT_CONTENT_CARDS_SETTINGS,
  normalizeContentCardsTargetPages,
  pickPrimaryContentCardsTargetPage,
  resolveContentCardsTargetPages,
  type ContentCardsDialogState,
  type ContentCardsInsertAfter,
  type ContentCardsPageInsertAfter,
  type ContentCardsPageOption,
  type ContentCardsPayload,
  type ContentCardsTargetPage,
  type ContentCardVariant
} from './contentCards/types';
import {
  loadContentCardsBlockDraft,
  saveContentCardsBlockDraft
} from './contentCards/contentCardDraft';
import ImageLibrarySelectionModal from '../../accounting/inventory/ImageLibrarySelectionModal';
import {
  isPageContentPlacement,
  mergeCorporatePagePositionOptions,
  resolvePageContentPlacementLabel
} from './contentCards/pageContentPlacements';

interface HomePageContentCardsPanelProps {
  companyId: string;
  configId: string;
  homepageModules?: CustomModule[];
  /** promo: Pazarlama > Kart Oluştur — sade banner şablonları */
  mode?: 'default' | 'promo';
  hideGroupedList?: boolean;
  onProvideActions?: (actions: { openEditor: (moduleId?: string | null) => void }) => void;
  onBlocksChanged?: () => void;
}

interface StoredContentCards extends ContentCardsPayload {
  enabled: boolean;
  sourceCampaignId?: string;
}

interface ContentCardsBlockRecord {
  moduleId: string;
  targetPage: ContentCardsTargetPage;
  targetPages: ContentCardsTargetPage[];
  displayOrder: number;
  enabled: boolean;
  payload: StoredContentCards;
  sourceCampaignId?: string;
}

interface SiblingPageBlock {
  moduleId: string;
  targetPage: string;
  title: string;
  kind: 'cards' | 'tags';
}

/** Sekmeli sayfa tipleri: admin hedef seçeneği + API pageType eşlemesi */
const TAB_TARGET_CONFIGS = [
  { prefix: 'about-tab', pageType: 'AboutTab', group: 'Hakkımızda' },
  { prefix: 'services-tab', pageType: 'ServiceTab', group: 'Hizmetler' },
  { prefix: 'product-solution-tab', pageType: 'ProductSolutionTab', group: 'Ürün ve Çözümler' },
  { prefix: 'reference-tab', pageType: 'ReferenceTab', group: 'Referanslar' }
] as const;

const HOMEPAGE_OPTION: ContentCardsPageOption = { value: 'homepage', label: 'Ana Sayfa', group: 'Ana Sayfa' };
const CONTACT_PAGE_OPTION: ContentCardsPageOption = {
  value: 'contact',
  label: 'İletişim sayfası',
  group: 'İletişim'
};

const parseStoredContentCards = (settingsJson?: string | null): StoredContentCards | null => {
  if (!settingsJson) return null;
  try {
    const parsed = JSON.parse(settingsJson);
    const payload = cleanContentCardsPayload({
      title: parsed?.title,
      cards: parsed?.cards || [],
      settings: parsed?.settings || DEFAULT_CONTENT_CARDS_SETTINGS,
      position: parsed?.position
    });
    return {
      ...payload,
      enabled: parsed?.enabled !== false,
      sourceCampaignId: String(parsed?.sourceCampaignId || '').trim() || undefined
    };
  } catch {
    return null;
  }
};

const tabLabel = (tab: any): string => String(tab?.title || tab?.name || 'İsimsiz Sekme');

/** Soft-delete / gizli sekmeler listeye girmez */
const isTabActive = (tab: any): boolean => tab?.isActive !== false && tab?.IsActive !== false;

/** Yalnızca aktif ana sekmeler ve onların aktif alt sekmeleri (yetim/silinmiş parent altları hariç) */
const buildTabOptions = (tabs: any[], prefix: string, group: string): ContentCardsPageOption[] => {
  const sorted = [...tabs]
    .filter(isTabActive)
    .sort((a, b) => (Number(a?.displayOrder) || 0) - (Number(b?.displayOrder) || 0));
  const mains = sorted.filter((tab) => !tab?.parentId);
  const mainIds = new Set(mains.map((main) => String(main.id)));
  const subsByParent = new Map<string, any[]>();
  sorted
    .filter((tab) => tab?.parentId && mainIds.has(String(tab.parentId)))
    .forEach((tab) => {
      const list = subsByParent.get(String(tab.parentId)) || [];
      list.push(tab);
      subsByParent.set(String(tab.parentId), list);
    });

  const options: ContentCardsPageOption[] = [];
  mains.forEach((main) => {
    options.push({
      value: `${prefix}:${main.id}` as ContentCardsTargetPage,
      label: tabLabel(main),
      group
    });
    (subsByParent.get(String(main.id)) || []).forEach((sub) => {
      options.push({
        value: `${prefix}:${sub.id}` as ContentCardsTargetPage,
        label: `${tabLabel(main)} › ${tabLabel(sub)}`,
        group
      });
    });
  });

  return options;
};

const HomePageContentCardsPanel: React.FC<HomePageContentCardsPanelProps> = ({
  companyId,
  configId,
  homepageModules = [],
  mode = 'default',
  hideGroupedList = false,
  onProvideActions,
  onBlocksChanged
}) => {
  const isPromoMode = mode === 'promo';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [storedBlocks, setStoredBlocks] = useState<ContentCardsBlockRecord[]>([]);
  const [siblingTagBlocks, setSiblingTagBlocks] = useState<SiblingPageBlock[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [editingModuleId, setEditingModuleId] = useState<string | null>(null);
  const [pageTargetOptions, setPageTargetOptions] = useState<ContentCardsPageOption[]>([HOMEPAGE_OPTION]);
  const [dialogState, setDialogState] = useState<ContentCardsDialogState>(DEFAULT_CONTENT_CARDS_DIALOG);
  const [selectedTemplate, setSelectedTemplate] = useState<ContentCardVariant>(
    isPromoMode ? 'banner' : 'minimal'
  );
  const [imageLibraryPicker, setImageLibraryPicker] = useState<{
    open: boolean;
    targetIndex: number;
    field: 'imageUrl' | 'brandLogoUrl';
  }>({
    open: false,
    targetIndex: -1,
    field: 'imageUrl'
  });
  const [snack, setSnack] = useState<{ open: boolean; type: 'success' | 'error'; message: string }>({
    open: false,
    type: 'success',
    message: ''
  });
  const legacyMigrationDoneRef = useRef(false);
  const editingModuleIdRef = useRef<string | null>(null);
  const storedBlocksRef = useRef<ContentCardsBlockRecord[]>([]);
  editingModuleIdRef.current = editingModuleId;
  storedBlocksRef.current = storedBlocks;

  const positionOptions = useMemo(() => {
    const moduleOptions = homepageModules
      .filter((module) => module.tabName !== 'İçerik Kartları')
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((module) => ({
        value: module.id,
        label: `${module.tabName} modülünden sonra`
      }));

    const cardAfter = storedBlocks
      .filter(
        (block) =>
          (block.targetPages.includes('homepage') || block.targetPage === 'homepage') &&
          block.moduleId !== editingModuleId &&
          block.enabled
      )
      .map((block) => ({
        value: block.moduleId,
        label: `"${block.payload.title || 'Kart yapısı'}" kartlarının altında`
      }));

    const tagAfter = siblingTagBlocks
      .filter((block) => block.targetPage === 'homepage')
      .map((block) => ({
        value: block.moduleId,
        label: `"${block.title}" etiketlerinin altında`
      }));

    return [
      { value: 'categories', label: 'Kategorilerden sonra' },
      ...moduleOptions,
      ...cardAfter,
      ...tagAfter,
      { value: 'end', label: 'Tüm modüllerden sonra (en alta)' }
    ];
  }, [homepageModules, storedBlocks, siblingTagBlocks, editingModuleId]);

  const pagePositionOptions = useMemo(() => {
    const selectedPages = normalizeContentCardsTargetPages(
      dialogState.targetPages || [dialogState.targetPage || 'homepage']
    );
    const otherPages = selectedPages.filter((page) => page !== 'homepage');
    if (otherPages.length === 0) return [];

    const options = mergeCorporatePagePositionOptions(otherPages);

    storedBlocks
      .filter(
        (block) =>
          otherPages.some((page) => block.targetPages.includes(page) || block.targetPage === page) &&
          block.moduleId !== editingModuleId &&
          block.enabled
      )
      .forEach((block) => {
        const title = block.payload.title || `Kart yapısı (${block.payload.cards.length} kart)`;
        options.push({
          value: block.moduleId,
          label: `"${title}" kart yapısının altında`
        });
      });

    siblingTagBlocks
      .filter((block) => otherPages.includes(block.targetPage as ContentCardsTargetPage))
      .forEach((block) => {
        options.push({
          value: block.moduleId,
          label: `"${block.title}" etiketlerinin altında`
        });
      });

    return options;
  }, [dialogState.targetPage, dialogState.targetPages, storedBlocks, siblingTagBlocks, editingModuleId]);

  const resolveHomepageDisplayOrder = (nextInsertAfter: ContentCardsInsertAfter): number => {
    const sortedModules = [...homepageModules]
      .filter((module) => module.tabName !== 'İçerik Kartları')
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    if (nextInsertAfter === 'categories') {
      const firstOrder = sortedModules[0]?.order ?? 0;
      return Math.max(0, firstOrder - 1);
    }

    if (nextInsertAfter === 'end') {
      const lastOrder = sortedModules[sortedModules.length - 1]?.order ?? 0;
      return lastOrder + 1;
    }

    const anchorIndex = sortedModules.findIndex((module) => module.id === nextInsertAfter);
    if (anchorIndex < 0) return 0;

    const anchorOrder = sortedModules[anchorIndex]?.order ?? anchorIndex;
    const nextOrder = sortedModules[anchorIndex + 1]?.order;
    if (typeof nextOrder === 'number') {
      return anchorOrder + (nextOrder - anchorOrder) / 2;
    }
    return anchorOrder + 1;
  };

  const resolveNextHomepageOrderForAnchor = (
    blocks: ContentCardsBlockRecord[],
    anchor: ContentCardsInsertAfter
  ): number => {
    const sameAnchorBlocks = blocks.filter(
      (block) =>
        (block.targetPages.includes('homepage') || block.targetPage === 'homepage') &&
        (block.payload.position?.insertAfter || 'categories') === anchor
    );

    if (sameAnchorBlocks.length === 0) {
      return resolveHomepageDisplayOrder(anchor);
    }

    return Math.max(...sameAnchorBlocks.map((block) => block.displayOrder ?? 0)) + 1;
  };

  const resolveNextOrderForTarget = (
    blocks: ContentCardsBlockRecord[],
    targetPage: ContentCardsTargetPage
  ): number => {
    const sameTarget = blocks.filter(
      (block) => block.targetPages.includes(targetPage) || block.targetPage === targetPage
    );
    if (sameTarget.length === 0) return 0;
    return Math.max(...sameTarget.map((block) => block.displayOrder ?? 0)) + 1;
  };

  const fetchModuleBlocks = async (): Promise<ContentCardsBlockRecord[]> => {
    const response = await getProductModules(companyId, 'content-cards');
    const rows = Array.isArray(response?.data) ? response.data : [];
    const blocks = rows
      .map((row: any) => {
        const parsed = parseStoredContentCards(row?.settingsJson);
        if (!parsed) return null;
        const targetPages = resolveContentCardsTargetPages(parsed.position);
        return {
          moduleId: String(row.id),
          targetPage: pickPrimaryContentCardsTargetPage(targetPages),
          targetPages,
          displayOrder: Number(row.displayOrder) || 0,
          enabled: row.isActive !== false && parsed.enabled !== false,
          payload: parsed,
          sourceCampaignId: parsed.sourceCampaignId
        } satisfies ContentCardsBlockRecord;
      })
      .filter(Boolean) as ContentCardsBlockRecord[];

    blocks.sort((a, b) => a.displayOrder - b.displayOrder);
    return blocks;
  };

  const persistBlockModule = async (
    payload: ContentCardsPayload,
    options: {
      moduleId?: string | null;
      targetPage: ContentCardsTargetPage;
      targetPages?: ContentCardsTargetPage[];
      insertAfter: ContentCardsInsertAfter;
      pageInsertAfter?: ContentCardsPageInsertAfter;
      displayOrder: number;
      isActive: boolean;
      sourceCampaignId?: string;
    }
  ) => {
    const targetPages = normalizeContentCardsTargetPages(options.targetPages || [options.targetPage], options.targetPage);
    const settingsJson = JSON.stringify({
      enabled: options.isActive,
      title: payload.title,
      cards: payload.cards,
      settings: payload.settings,
      position: {
        insertAfter: options.insertAfter,
        targetPage: pickPrimaryContentCardsTargetPage(targetPages),
        targetPages,
        ...(options.pageInsertAfter ? { pageInsertAfter: options.pageInsertAfter } : {})
      },
      ...(options.sourceCampaignId ? { sourceCampaignId: options.sourceCampaignId } : {})
    });

    return saveProductModule({
      id: options.moduleId || undefined,
      companyId,
      configId,
      moduleType: 'content-cards',
      tabName: 'İçerik Kartları',
      sectionTitle: payload.title || 'İçerik Kartları',
      displayOrder: options.displayOrder,
      isActive: options.isActive,
      settingsJson,
      selectedProductIds: JSON.stringify([]),
      selectedCategoryIds: JSON.stringify([]),
      maxCategories: 0,
      horizontalPosition: 50,
      itemsPerRow: 3
    });
  };

  /**
   * Eski model: kartlar sayfa HTML'inin içine gömülüyordu. Panelde listelenip
   * yönetilebilmeleri için gömülü blokları modüle taşıyıp HTML'den temizler.
   */
  const migrateLegacyEmbeddedBlocks = async (
    tabsByConfig: Array<{ prefix: string; pageType: string; tabs: any[] }>,
    customPages: any[],
    existingBlocks: ContentCardsBlockRecord[]
  ): Promise<boolean> => {
    let migrated = false;
    let nextOrderBase = existingBlocks.length > 0
      ? Math.max(...existingBlocks.map((block) => block.displayOrder ?? 0)) + 1
      : 0;

    for (const { prefix, pageType, tabs } of tabsByConfig) {
      for (const tab of tabs) {
        const content = String(tab?.content || '');
        const embedded = parseContentCardsFromHtml(content);
        if (!embedded || embedded.cards.length === 0) continue;

        const targetPage = `${prefix}:${tab.id}` as ContentCardsTargetPage;
        try {
          await persistBlockModule(embedded, {
            targetPage,
            insertAfter: 'content-end',
            displayOrder: nextOrderBase++,
            isActive: true
          });
          await saveAboutTab({
            id: tab.id,
            configId,
            companyId,
            pageType,
            name: tab.name,
            title: tab.title,
            content: stripContentCardsFromHtml(content),
            backgroundColor: tab.backgroundColor,
            displayOrder: tab.displayOrder,
            isActive: tab.isActive,
            sections: tab.sections,
            parentId: tab.parentId,
            level: tab.level
          });
          migrated = true;
        } catch (error) {
          console.error('[CONTENT CARDS] Gömülü kart bloğu taşınamadı:', targetPage, error);
        }
      }
    }

    for (const page of customPages) {
      const content = String(page?.content || '');
      const embedded = parseContentCardsFromHtml(content);
      if (!embedded || embedded.cards.length === 0) continue;

      const targetPage = `custom-page:${page.id}` as ContentCardsTargetPage;
      try {
        await persistBlockModule(embedded, {
          targetPage,
          insertAfter: 'content-end',
          displayOrder: nextOrderBase++,
          isActive: true
        });
        await saveCustomPage({
          id: page.id,
          configId,
          title: page.title,
          slug: page.slug,
          content: stripContentCardsFromHtml(content),
          metaTitle: page.metaTitle,
          metaDescription: page.metaDescription,
          name: page.name,
          isActive: page.isActive,
          showInMenu: page.showInMenu,
          showInFooter: page.showInFooter,
          displayOrder: page.displayOrder
        });
        migrated = true;
      } catch (error) {
        console.error('[CONTENT CARDS] Gömülü kart bloğu taşınamadı:', targetPage, error);
      }
    }

    return migrated;
  };

  const loadAll = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      // Varsayılan ana bölümler her zaman listelenmeli (tab olmasa bile).
      let tabsByConfig: Array<{ prefix: string; pageType: string; group: string; tabs: any[] }> =
        TAB_TARGET_CONFIGS.map((cfg) => ({ ...cfg, tabs: [] as any[] }));
      let customPages: any[] = [];

      if (configId) {
        const tabResponses = await Promise.all(
          TAB_TARGET_CONFIGS.map((cfg) =>
            getAboutTabs(configId, cfg.pageType)
              .then((res: any) => (Array.isArray(res?.data) ? res.data : []))
              .catch(() => [])
          )
        );
        tabsByConfig = TAB_TARGET_CONFIGS.map((cfg, index) => ({ ...cfg, tabs: tabResponses[index] }));

        customPages = await getCustomPages(configId)
          .then((res: any) => (Array.isArray(res?.data) ? res.data : []))
          .catch(() => []);
      }

      const options: ContentCardsPageOption[] = [
        HOMEPAGE_OPTION,
        // Her ana bölüm için önce bölümün kendi (ana) sayfası, sonra oluşturulan alt sekmeleri gelir.
        ...tabsByConfig.flatMap(({ tabs, prefix, group }) => [
          {
            value: `${prefix}:main` as ContentCardsTargetPage,
            label: `${group} sayfası`,
            group
          },
          ...buildTabOptions(tabs, prefix, group)
        ]),
        CONTACT_PAGE_OPTION,
        ...customPages
          .filter((page: any) => page?.isActive !== false && page?.IsActive !== false)
          .map((page: any) => ({
            value: `custom-page:${page.id}` as ContentCardsTargetPage,
            label: String(page.title || page.slug || 'Özel Sayfa'),
            group: 'Özel Sayfalar'
          }))
      ];
      setPageTargetOptions(options);

      let blocks = await fetchModuleBlocks();

      if (configId && !legacyMigrationDoneRef.current) {
        legacyMigrationDoneRef.current = true;
        const migrated = await migrateLegacyEmbeddedBlocks(tabsByConfig, customPages, blocks);
        if (migrated) {
          blocks = await fetchModuleBlocks();
        }
      }

      setStoredBlocks(blocks);
      setEnabled(
        (isPromoMode ? blocks : blocks.filter((block) => !block.sourceCampaignId)).some((block) => block.enabled)
      );

      const tagModulesResponse = await getProductModules(companyId, 'content-tags').catch(() => null);
      const tagRows = Array.isArray(tagModulesResponse?.data) ? tagModulesResponse.data : [];
      const tagSiblings: SiblingPageBlock[] = tagRows
        .map((row: any) => {
          try {
            const parsed = JSON.parse(row?.settingsJson || '{}');
            if (parsed?.enabled === false || row?.isActive === false) return null;
            const tags = Array.isArray(parsed?.tags) ? parsed.tags : [];
            if (tags.length === 0) return null;
            return {
              moduleId: String(row.id),
              targetPage: String(parsed?.position?.targetPage || 'homepage'),
              title: String(parsed?.title || '').trim() || `Etiket bloğu (${tags.length} etiket)`,
              kind: 'tags' as const
            };
          } catch {
            return null;
          }
        })
        .filter(Boolean) as SiblingPageBlock[];
      setSiblingTagBlocks(tagSiblings);
    } catch (error) {
      console.error('[CONTENT CARDS] Yükleme hatası:', error);
      setSnack({ open: true, type: 'error', message: 'İçerik kartları yüklenemedi.' });
    } finally {
      setLoading(false);
    }
  }, [companyId, configId, isPromoMode]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const buildCardsFromTemplate = (variant: ContentCardVariant) => {
    const template = getContentCardTemplate(variant);
    return template.sampleCards.map((card) =>
      normalizeContentCardForEditor({
        ...DEFAULT_CONTENT_CARDS_DIALOG.cards[0],
        ...card,
        bulletItems: Array.isArray(card.bulletItems) ? [...card.bulletItems] : []
      })
    );
  };

  const openEditor = (moduleId: string | null = null) => {
    setEditingModuleId(moduleId);

    if (moduleId) {
      const block = storedBlocks.find((item) => item.moduleId === moduleId);
      if (!block) return;

      const base = cleanContentCardsPayload(block.payload);
      const activeVariant = normalizeContentCardVariant(base.settings.variant);
      setSelectedTemplate(activeVariant);
      setDialogState({
        open: true,
        isEdit: true,
        title: base.title || '',
        settings: base.settings,
        cards: base.cards.length > 0
          ? base.cards.map(normalizeContentCardForEditor)
          : buildCardsFromTemplate(activeVariant),
        insertAfter: base.position?.insertAfter || (block.targetPage === 'homepage' ? 'categories' : 'content-end'),
        targetPage: block.targetPage,
        targetPages: block.targetPages,
        pageInsertAfter: base.position?.pageInsertAfter || 'content-end'
      });
      return;
    }

    const template = getContentCardTemplate(isPromoMode ? 'banner' : selectedTemplate);
    const activeVariant = isPromoMode ? 'banner' : template.settings.variant;
    const promoCards = isPromoMode
      ? (template.sampleCards.length >= 3
          ? template.sampleCards.slice(0, 3).map((sample, i) => ({
              ...DEFAULT_CONTENT_CARD,
              ...sample,
              title: '',
              description: '',
              date: '',
              imageUrl: '',
              panelColor: sample.panelColor || BANNER_PANEL_COLORS[i % BANNER_PANEL_COLORS.length]
            }))
          : [0, 1, 2].map((i) => ({
              ...DEFAULT_CONTENT_CARD,
              panelColor: BANNER_PANEL_COLORS[i % BANNER_PANEL_COLORS.length],
              align: 'left' as const
            })))
      : activeVariant === 'values-strip' || activeVariant === 'feature-grid' || activeVariant === 'offer-split'
        ? buildCardsFromTemplate(activeVariant)
        : [{ ...DEFAULT_CONTENT_CARD }];

    setDialogState({
      open: true,
      isEdit: false,
      title: isPromoMode ? 'Kampanyalar' : '',
      settings: {
        ...template.settings,
        variant: activeVariant,
        layoutTemplate: isPromoMode ? 'promo-trio' : template.settings.layoutTemplate,
        cardRadiusPx: isPromoMode ? 18 : template.settings.cardRadiusPx,
        showBullets: isPromoMode ? false : template.settings.showBullets,
        showDate: isPromoMode ? true : template.settings.showDate
      },
      cards: promoCards,
      insertAfter: 'categories',
      targetPage: 'homepage',
      targetPages: ['homepage'],
      pageInsertAfter: 'content-end'
    });
  };

  useEffect(() => {
    onProvideActions?.({ openEditor });
  }, [onProvideActions, storedBlocks]);

  const applyDialogTargetPages = (targetPages: ContentCardsTargetPage[]) => {
    setDialogState((prev) => {
      const pages = normalizeContentCardsTargetPages(targetPages, prev.targetPage);
      const nextPrimary = pickPrimaryContentCardsTargetPage(pages);
      const includesHomepage = pages.includes('homepage');
      const wasHomepage = (prev.targetPages || [prev.targetPage]).includes('homepage');
      const otherPageOptions = mergeCorporatePagePositionOptions(pages.filter((page) => page !== 'homepage'));
      const keepPageInsert =
        otherPageOptions.some((option) => option.value === prev.insertAfter) ||
        otherPageOptions.some((option) => option.value === prev.pageInsertAfter);
      if (includesHomepage === wasHomepage) {
        return {
          ...prev,
          targetPage: nextPrimary,
          targetPages: pages,
          insertAfter: includesHomepage
            ? prev.insertAfter
            : keepPageInsert
              ? prev.insertAfter
              : 'content-end',
          pageInsertAfter: keepPageInsert ? prev.pageInsertAfter : 'content-end'
        };
      }
      return {
        ...prev,
        targetPage: nextPrimary,
        targetPages: pages,
        insertAfter: includesHomepage ? 'categories' : 'content-end',
        pageInsertAfter: 'content-end'
      };
    });
  };

  const resolveTargetLabel = (targetPage: ContentCardsTargetPage): string =>
    pageTargetOptions.find((option) => option.value === targetPage)?.label ||
    (targetPage === 'homepage' ? 'Ana Sayfa' : 'Silinmiş sayfa');

  const resolvePositionLabel = (block: ContentCardsBlockRecord): string => {
    const insertAfter = block.payload.position?.insertAfter || 'categories';
    if (block.targetPage !== 'homepage') {
      const placementLabel = resolvePageContentPlacementLabel(insertAfter);
      if (placementLabel) return placementLabel;
      const cardSibling = storedBlocks.find((item) => item.moduleId === insertAfter);
      if (cardSibling) {
        const title =
          cardSibling.payload.title || `Kart yapısı (${cardSibling.payload.cards.length} kart)`;
        return `"${title}" kart yapısının altında`;
      }
      const tagSibling = siblingTagBlocks.find((item) => item.moduleId === insertAfter);
      if (tagSibling) return `"${tagSibling.title}" etiketlerinin altında`;
      return 'İçeriğin altında';
    }
    return positionOptions.find((option) => option.value === insertAfter)?.label || 'Kategorilerden sonra';
  };

  const persistFromDialog = useCallback(
    async (
      state: ContentCardsDialogState,
      options?: { requireTitles?: boolean; closeAfter?: boolean; showSnack?: boolean }
    ): Promise<string | null> => {
      if (!companyId || !configId || !state.open) return editingModuleIdRef.current;

      const targetPages = normalizeContentCardsTargetPages(
        state.targetPages || [state.targetPage || 'homepage']
      );
      const targetPage = pickPrimaryContentCardsTargetPage(targetPages);
      const includesHomepage = targetPages.includes('homepage');
      const includesOther = targetPages.some((page) => page !== 'homepage');
      const selectedInsertAfter = String(state.insertAfter || '').trim();
      const insertAfter: ContentCardsInsertAfter = includesHomepage
        ? state.insertAfter
        : selectedInsertAfter === 'content-end'
          ? 'content-end'
          : selectedInsertAfter
            ? (selectedInsertAfter as ContentCardsInsertAfter)
            : 'content-end';
      const pageInsertAfter: ContentCardsPageInsertAfter | undefined = includesOther
        ? includesHomepage
          ? isPageContentPlacement(state.pageInsertAfter)
            ? state.pageInsertAfter
            : 'content-end'
          : isPageContentPlacement(insertAfter)
            ? insertAfter
            : 'content-end'
        : undefined;

      const payload = cleanContentCardsPayload({
        title: state.title,
        cards: state.cards,
        settings: state.settings,
        position: { insertAfter, targetPage, targetPages, pageInsertAfter }
      });

      const moduleId = editingModuleIdRef.current;
      const hasMeaningfulData =
        Boolean(String(state.title || '').trim()) || payload.cards.some((card) => hasContentCardData(card));

      if (payload.cards.length === 0 || (!moduleId && !hasMeaningfulData)) {
        if (options?.requireTitles) {
          setSnack({
            open: true,
            type: 'error',
            message: 'En az bir kartın başlığını girmelisiniz. Diğer alanlar opsiyoneldir.'
          });
        }
        return moduleId;
      }

      if (options?.requireTitles) {
        const cardWithoutTitle = payload.cards.findIndex((card) => !String(card.title || '').trim());
        if (cardWithoutTitle >= 0) {
          setSnack({
            open: true,
            type: 'error',
            message: `Kart #${cardWithoutTitle + 1} için başlık zorunludur. Diğer alanlar opsiyoneldir.`
          });
          return null;
        }
      }

      const existingBlock = moduleId
        ? storedBlocksRef.current.find((block) => block.moduleId === moduleId)
        : null;
      const otherBlocks = moduleId
        ? storedBlocksRef.current.filter((block) => block.moduleId !== moduleId)
        : storedBlocksRef.current;

      let nextDisplayOrder: number;
      if (includesHomepage) {
        const previousInsertAfter = existingBlock?.payload.position?.insertAfter || 'categories';
        const keepOrder =
          existingBlock && existingBlock.targetPage === 'homepage' && previousInsertAfter === insertAfter;
        nextDisplayOrder = keepOrder
          ? existingBlock.displayOrder
          : resolveNextHomepageOrderForAnchor(otherBlocks, insertAfter);
      } else {
        nextDisplayOrder =
          existingBlock && existingBlock.targetPage === targetPage
            ? existingBlock.displayOrder
            : resolveNextOrderForTarget(otherBlocks, targetPage);
      }

      const response = await persistBlockModule(payload, {
        moduleId,
        targetPage,
        targetPages,
        insertAfter,
        pageInsertAfter,
        displayOrder: nextDisplayOrder,
        isActive: existingBlock ? existingBlock.enabled : true,
        sourceCampaignId: existingBlock?.sourceCampaignId
      });
      const savedId = String(response?.data?.id || response?.data?.Id || moduleId || '');
      if (savedId && savedId !== moduleId) {
        editingModuleIdRef.current = savedId;
        setEditingModuleId(savedId);
        setDialogState((prev) => ({ ...prev, isEdit: true }));
      }

      if (savedId) {
        const nextRecord: ContentCardsBlockRecord = {
          moduleId: savedId,
          targetPage,
          targetPages,
          displayOrder: nextDisplayOrder,
          enabled: existingBlock ? existingBlock.enabled : true,
          payload: { ...payload, enabled: existingBlock ? existingBlock.enabled : true, sourceCampaignId: existingBlock?.sourceCampaignId },
          sourceCampaignId: existingBlock?.sourceCampaignId
        };
        setStoredBlocks((prev) => {
          const exists = prev.some((block) => block.moduleId === savedId);
          return exists
            ? prev.map((block) => (block.moduleId === savedId ? nextRecord : block))
            : [...prev, nextRecord];
        });
        setEnabled(true);
        onBlocksChanged?.();
      }

      if (options?.showSnack) {
        setSnack({
          open: true,
          type: 'success',
          message: moduleId
            ? `Kart bloğu güncellendi. Sayfalar: ${targetPages.map(resolveTargetLabel).join(', ')}`
            : `Yeni kart bloğu eklendi. Sayfalar: ${targetPages.map(resolveTargetLabel).join(', ')}`
        });
      } else {
        notifyEcommerceSaved('Kart bloğu kaydedildi');
      }

      if (options?.closeAfter) {
        setDialogState(DEFAULT_CONTENT_CARDS_DIALOG);
        setEditingModuleId(null);
        editingModuleIdRef.current = null;
      }

      return savedId || moduleId;
    },
    [companyId, configId, onBlocksChanged]
  );

  const persistDialogValue = useCallback(
    async (state: ContentCardsDialogState) => {
      await persistFromDialog(state);
    },
    [persistFromDialog]
  );

  const { flush: flushCardsDialog } = useEditorAutosave(dialogState, persistDialogValue, {
    enabled: dialogState.open,
    delayMs: 800
  });

  const closeCardsDialog = useCallback(async () => {
    try {
      await flushCardsDialog();
    } catch (error) {
      console.error('[CONTENT CARDS] Çıkışta kayıt hatası:', error);
      setSnack({ open: true, type: 'error', message: 'İçerik kartları kaydedilemedi.' });
      return;
    }
    setDialogState(DEFAULT_CONTENT_CARDS_DIALOG);
    setEditingModuleId(null);
    editingModuleIdRef.current = null;
  }, [flushCardsDialog]);

  const handleSaveDialog = async () => {
    if (!companyId || !configId) {
      setSnack({ open: true, type: 'error', message: 'Önce e-ticaret yapılandırmasını kaydedin.' });
      return;
    }

    setSaving(true);
    try {
      const savedId = await persistFromDialog(dialogState, {
        requireTitles: true,
        closeAfter: true,
        showSnack: true
      });
      if (!savedId && !editingModuleIdRef.current) {
        return;
      }
    } catch (error) {
      console.error('[CONTENT CARDS] Kayıt hatası:', error);
      setSnack({ open: true, type: 'error', message: 'İçerik kartları kaydedilemedi.' });
    } finally {
      setSaving(false);
    }
  };

  const panelBlocks = useMemo(
    () =>
      isPromoMode
        ? storedBlocks
        : storedBlocks.filter((block) => !block.sourceCampaignId),
    [storedBlocks, isPromoMode]
  );

  const persistBlocksEnabled = async (blocks: ContentCardsBlockRecord[], checked: boolean) => {
    setSaving(true);
    try {
      for (const block of blocks) {
        const payload = cleanContentCardsPayload(block.payload);
        await persistBlockModule(payload, {
          moduleId: block.moduleId,
          targetPage: block.targetPage,
          targetPages: block.targetPages,
          insertAfter: block.payload.position?.insertAfter || (block.targetPage === 'homepage' ? 'categories' : 'content-end'),
          pageInsertAfter: block.payload.position?.pageInsertAfter,
          displayOrder: block.displayOrder,
          isActive: checked,
          sourceCampaignId: block.sourceCampaignId
        });
      }
      await loadAll();
      onBlocksChanged?.();
      return true;
    } catch (error) {
      console.error('[CONTENT CARDS] Görünürlük güncelleme hatası:', error);
      setSnack({ open: true, type: 'error', message: 'Kart görünürlüğü güncellenemedi.' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleEnabledChange = async (checked: boolean) => {
    if (panelBlocks.length === 0 && checked) {
      openEditor(null);
      return;
    }

    if (panelBlocks.length === 0) {
      setEnabled(false);
      return;
    }

    const ok = await persistBlocksEnabled(panelBlocks, checked);
    if (ok) {
      setEnabled(checked);
      setSnack({
        open: true,
        type: 'success',
        message: checked
          ? 'Tüm kart blokları sitede gösterilecek.'
          : 'Tüm kart blokları gizlendi. Bloklar silinmedi; istediğinizde tekrar açabilirsiniz.'
      });
    }
  };

  const handleToggleBlock = async (block: ContentCardsBlockRecord) => {
    const ok = await persistBlocksEnabled([block], !block.enabled);
    if (ok) {
      setSnack({
        open: true,
        type: 'success',
        message: block.enabled ? 'Kart bloğu gizlendi.' : 'Kart bloğu sitede gösterilecek.'
      });
    }
  };

  const handleDeleteBlock = async (moduleId: string) => {
    if (!companyId) return;

    setSaving(true);
    try {
      await deleteProductModule({ id: moduleId, companyId });
      await loadAll();
      onBlocksChanged?.();
      setSnack({ open: true, type: 'success', message: 'Kart bloğu silindi.' });
    } catch (error) {
      console.error('[CONTENT CARDS] Silme hatası:', error);
      setSnack({ open: true, type: 'error', message: 'Kart bloğu silinemedi.' });
    } finally {
      setSaving(false);
    }
  };

  const groupedBlocks = useMemo(() => {
    const groups = new Map<string, ContentCardsBlockRecord[]>();
    panelBlocks.forEach((block) => {
      const pages = block.targetPages.length > 0 ? block.targetPages : [block.targetPage];
      pages.forEach((page) => {
        const label = resolveTargetLabel(page);
        const list = groups.get(label) || [];
        list.push(block);
        groups.set(label, list);
      });
    });

    return Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === 'Ana Sayfa') return -1;
      if (b === 'Ana Sayfa') return 1;
      return a.localeCompare(b, 'tr');
    });
  }, [panelBlocks, pageTargetOptions, positionOptions]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          İçerik kartları yükleniyor...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
        {!isPromoMode && (
          <>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Kart Stil Şablonu
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Kartların görsel stilini seçin. Yerleşim (sol panel + ızgara vb.) blok eklerken / düzenlerken
          seçilir; kartları istediğiniz gibi sütunlara dizebilirsiniz.
        </Typography>
        <ContentCardsStylePreview
          value={selectedTemplate}
          onChange={(variant) => {
            setSelectedTemplate(variant);
            if (dialogState.open) {
              const template = getContentCardTemplate(variant);
              // Şablon değiştirilince yalnızca stil / yerleşim / renkler yeni şablona geçer;
              // kullanıcının girdiği kart içerikleri (başlık, açıklama vb.) korunur.
              // Blok tamamen boşsa yeni şablonun örnek kartları yüklenir.
              setDialogState((prev) => {
                const hasUserCards = (prev.cards || []).some((card) => hasContentCardData(card));
                return {
                  ...prev,
                  settings: { ...template.settings },
                  ...(hasUserCards ? {} : { cards: buildCardsFromTemplate(variant) })
                };
              });
            }
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          Seçili şablon: <strong>{getContentCardTemplateLabel(selectedTemplate)}</strong>
        </Typography>
          </>
        )}
        {isPromoMode && (
          <>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Sade Kampanya Banner
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Daha büyük kart. Sol / orta / sağ hizayı kaydedebilirsiniz. Görsel yoksa solda indirim ve ad, sağda tarih ve CTA görünür.
            </Typography>
          </>
        )}
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(e) => handleEnabledChange(e.target.checked)}
            disabled={saving}
          />
        }
        label={isPromoMode ? 'Kampanya kartlarını sitede göster' : 'İçerik kartlarını sitede göster'}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
        {isPromoMode
          ? '“Kart Bloğu Oluştur” → 3 kart için görsel / başlık / renk seç → Kaydet.'
          : 'Kartları bir veya birden fazla sayfada gösterebilirsiniz. Aynı kartı taslak alıp başka bloğa yapıştırmak da mümkün. Düğmeyi kapattığınızda bloklar silinmez, yalnızca sitede gizlenir.'}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
        <Button
          variant="contained"
          startIcon={panelBlocks.length > 0 ? <AddIcon /> : <ViewCarouselOutlinedIcon />}
          onClick={() => openEditor(null)}
          disabled={saving}
        >
          {panelBlocks.length > 0
            ? (isPromoMode ? 'Yeni Promo Kart Bloğu' : 'Yeni Kart Bloğu Ekle')
            : (isPromoMode ? 'Promo Kart Bloğu Oluştur' : 'Kart Bloğu Oluştur')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<ContentPasteIcon />}
          disabled={saving}
          onClick={() => {
            const draft = loadContentCardsBlockDraft();
            if (!draft) {
              setSnack({ open: true, type: 'error', message: 'Yapıştırılacak kart bloğu taslağı yok.' });
              return;
            }
            openEditor(null);
            setDialogState((prev) => ({
              ...prev,
              open: true,
              isEdit: false,
              title: draft.title || prev.title,
              settings: draft.settings || prev.settings,
              cards: draft.cards.length > 0 ? draft.cards : prev.cards
            }));
            setSnack({
              open: true,
              type: 'success',
              message: 'Blok taslağı açıldı. Sayfaları seçip kaydedin.'
            });
          }}
        >
          Taslaktan yapıştır
        </Button>
      </Box>

      {!hideGroupedList && groupedBlocks.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {groupedBlocks.map(([groupLabel, blocks]) => (
            <Box key={groupLabel}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                {groupLabel}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {blocks.map((block, index) => {
                  const blockVariant = normalizeContentCardVariant(block.payload.settings.variant);
                  const variantLabel = getContentCardTemplateLabel(blockVariant);
                  const layoutLabel = getContentCardLayoutLabel(
                    normalizeContentCardsLayout(block.payload.settings.layoutTemplate)
                  );
                  const hideLayoutLabel = blockVariant === 'values-strip';
                  return (
                    <Alert
                      key={block.moduleId}
                      severity={block.enabled ? 'info' : 'warning'}
                      icon={block.enabled ? undefined : <VisibilityOffOutlinedIcon fontSize="inherit" />}
                      sx={{ alignItems: 'center' }}
                      action={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title={block.enabled ? 'Bloğu gizle' : 'Bloğu göster'}>
                            <Switch
                              size="small"
                              checked={block.enabled}
                              onChange={() => handleToggleBlock(block)}
                              disabled={saving}
                            />
                          </Tooltip>
                          <Tooltip title="Bloğu taslak al">
                            <IconButton
                              size="small"
                              onClick={() => {
                                const ok = saveContentCardsBlockDraft({
                                  title: block.payload.title,
                                  cards: block.payload.cards,
                                  settings: block.payload.settings
                                });
                                setSnack({
                                  open: true,
                                  type: ok ? 'success' : 'error',
                                  message: ok
                                    ? 'Kart bloğu taslak alındı. Yeni blokta “Taslaktan yapıştır” ile kullanın.'
                                    : 'Blok taslağı kaydedilemedi.'
                                });
                              }}
                              disabled={saving}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton size="small" onClick={() => openEditor(block.moduleId)} disabled={saving}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteBlock(block.moduleId)}
                            disabled={saving}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    >
                      <Typography variant="body2" component="span">
                        <strong>Blok {index + 1}:</strong> {block.payload.cards.length} kart
                        {block.payload.title ? ` · ${block.payload.title}` : ''}
                        {` · Stil: ${variantLabel}`}
                        {hideLayoutLabel ? '' : ` · Yerleşim: ${layoutLabel}`}
                        {` · Konum: ${resolvePositionLabel(block)}`}
                        {block.targetPages.length > 1
                          ? ` · ${block.targetPages.length} sayfada`
                          : ''}
                      </Typography>
                      {block.sourceCampaignId && (
                        <Chip label="Kampanyadan" size="small" color="primary" variant="outlined" sx={{ ml: 1, height: 20 }} />
                      )}
                      {!block.enabled && (
                        <Chip label="Gizli" size="small" color="warning" sx={{ ml: 1, height: 20 }} />
                      )}
                    </Alert>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <ContentCardsDialog
        state={dialogState}
        onClose={closeCardsDialog}
        onSave={handleSaveDialog}
        onStateChange={(updater) => setDialogState((prev) => updater(prev))}
        onOpenImageLibrary={(cardIndex, field = 'imageUrl') =>
          setImageLibraryPicker({ open: true, targetIndex: cardIndex, field })
        }
        onTargetPagesChange={applyDialogTargetPages}
        onNotify={(type, message) => setSnack({ open: true, type, message })}
        pageTargetOptions={pageTargetOptions}
        positionOptions={positionOptions}
        pagePositionOptions={pagePositionOptions}
        simpleMode={isPromoMode}
        configId={configId}
      />

      <ImageLibrarySelectionModal
        open={imageLibraryPicker.open}
        onClose={() => setImageLibraryPicker({ open: false, targetIndex: -1, field: 'imageUrl' })}
        onSave={(selectedUrls) => {
          const selectedUrl = selectedUrls?.[0] || '';
          if (!selectedUrl || imageLibraryPicker.targetIndex < 0) {
            setImageLibraryPicker({ open: false, targetIndex: -1, field: 'imageUrl' });
            return;
          }
          const field = imageLibraryPicker.field || 'imageUrl';
          setDialogState((prev) => ({
            ...prev,
            cards: prev.cards.map((card, idx) =>
              idx === imageLibraryPicker.targetIndex ? { ...card, [field]: selectedUrl } : card
            )
          }));
          setImageLibraryPicker({ open: false, targetIndex: -1, field: 'imageUrl' });
        }}
        companyId={companyId}
        productName="İçerik Kartları"
        currentImageUrls={[]}
        productId={null}
        singleSelect
        maxSelection={1}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          severity={snack.type}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HomePageContentCardsPanel;
